// AdministrarCarrerasCatalog.jsx - Catálogo de carreras del instructor con grid layout estilo moderno
import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE } from '../../../../../api';
import { ENDPOINTS as LMS_ENDPOINTS } from '../../../Utils/Endpoints';
import { Models as LmsModels } from '../../../Utils/ModelsLMS';
import { getSessionUserId } from '../../../../../utils/SessionUser';
import sigmaDelete from '../../../../Pages/img/Borrar.png';
import {
    FiClock,
    FiCheckCircle,
    FiSearch,
    FiPlusSquare,
    FiLayers,
} from 'react-icons/fi';
import CarrerasModal from '../Carreras/CarrerasModal';
import ConfirmDeleteDialog from '../../../../security/ConfirmDeleteDialog';
import AlertDialog from '../../Shared/AlertDialog';

const nombreTabla = "Mis Carreras";

export default function AdministrarCarrerasCatalog() {
    const navigate = useNavigate();
    const SERVER_URL = API_BASE.replace(/\/api\/?$/, '');
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState([]);
    const [tab, setTab] = useState('todo');
    const [searchTerm, setSearchTerm] = useState('');

    // Estados para el modal
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [modalMode, setModalMode] = useState('new');
    const [idInstructor, setIdInstructor] = useState(null);

    // Estado para el diálogo de confirmación de eliminación
    const [deleteDialog, setDeleteDialog] = useState({
        open: false,
        item: null,
    });
    const [alertConfig, setAlertConfig] = useState({ open: false, message: '', title: 'Información', variant: 'info' });

    const triggerAlert = (message, variant = 'error', title = 'Error') => {
        setModalOpen(false);
        setDeleteDialog({ open: false, item: null });
        setAlertConfig({ open: true, message, variant, title });
    };

    // Cargar datos desde BD
    const loadData = async () => {
        setLoading(true);
        try {
            const sessionUserId = getSessionUserId();
            if (!sessionUserId) {
                setRows([]);
                setLoading(false);
                return;
            }

            // 1. Obtener Instructor — si no existe, la lista queda vacía
            let currentIdInstructor = null;
            try {
                const resInstructor = await axios.get(LMS_ENDPOINTS.instructorByUsuario(sessionUserId));
                if (resInstructor.data) {
                    const instructor = LmsModels.instructor(resInstructor.data);
                    currentIdInstructor = instructor.idInstructor ?? instructor.IdInstructor;
                }
            } catch {
                console.warn('[AdministrarCarreras] No se encontró perfil de instructor para este usuario.');
            }

            if (!currentIdInstructor) {
                console.warn('[AdministrarCarreras] Sin instructor → lista vacía.');
                setIdInstructor(null);
                setRows([]);
                return;
            }

            setIdInstructor(currentIdInstructor);
            console.log('[AdministrarCarreras] Instructor actual:', currentIdInstructor);

            // 2. Obtener Carreras del Instructor
            const resCarreras = await axios.get(LMS_ENDPOINTS.carrerasByInstructor(currentIdInstructor));
            const todasCarreras = (resCarreras.data || []).map(LmsModels.carrera);

            // 3. Filtrar además por empresa de sesión para garantizar aislamiento multi-tenant.
            // Si la carrera no tiene IdEmpresa (global), solo el instructor SuperAdmin la ve.
            const { getSessionEmpresaId, isSessionSuperAdmin } = await import('../../../../../utils/SessionUser');
            const idEmpresaActual = getSessionEmpresaId();
            const esSuperAdmin = isSessionSuperAdmin();

            const carrerasFiltradas = esSuperAdmin
                ? todasCarreras
                : todasCarreras.filter(c => {
                    const idEmpresaCarrera = c.idEmpresa ?? c.IdEmpresa ?? null;
                    // Mostrar solo si la carrera pertenece a mi empresa actual
                    return String(idEmpresaCarrera) === String(idEmpresaActual);
                });

            console.log('[AdministrarCarreras] Total instructor:', todasCarreras.length, '→ De mi empresa:', carrerasFiltradas.length);
            setRows(carrerasFiltradas);
        } catch (error) {
            console.error('Error al cargar carreras:', error);
            setRows([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, []);

    // Filtrado y Ordenamiento
    const filtered = useMemo(() => {
        let data = [...rows];

        // 1. Filtro de tab
        if (tab === 'aprobada') {
            data = data.filter(c => !c.publicado && c.idCertificado !== null);
        } else if (tab === 'en-curso') {
            data = data.filter(c => !c.publicado && c.idCertificado === null);
        } else if (tab === 'finalizado') {
            data = data.filter(c => c.publicado);
        }

        // 2. Filtro de búsqueda
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            data = data.filter(c => (c.nombre || '').toLowerCase().includes(term));
        }

        // 3. Ordenamiento: Aprobadas (publicadas) primero, luego borradores/en-curso
        data.sort((a, b) => {
            const getPriority = (carrera) => {
                if (carrera.publicado) return 1; // Publicadas / Aprobadas
                return 2; // Borradores / En Curso
            };
            return getPriority(a) - getPriority(b);
        });

        return data;
    }, [rows, tab, searchTerm]);

    const handleCreate = () => {
        setModalMode('new');
        setSelectedId(null);
        setModalOpen(true);
    };


    const handleDelete = (carrera) => {
        setDeleteDialog({
            open: true,
            item: carrera,
        });
    };

    const confirmDelete = async () => {
        if (!deleteDialog.item) return;
        try {
            await axios.delete(LMS_ENDPOINTS.carrerasById(deleteDialog.item.idCarrera));
            setDeleteDialog({ open: false, item: null });
            await loadData();
        } catch (error) {
            console.error("Error al eliminar carrera:", error);
            triggerAlert("No se pudo eliminar la carrera.");
        }
    };

    const handleSubmit = async (form, mode) => {
        try {
            const payload = {
                IdCarrera: mode === 'new' ? 0 : selectedId,
                Nombre: form.nombre,
                Descripcion: form.descripcion,
                IdInstructor: idInstructor,
                IdCertificado: null,
                Activado: true,
                UrlImagen: form.urlImagen,
                Publicado: form.publicado
            };

            if (mode === 'new') {
                await axios.post(LMS_ENDPOINTS.carreras, payload);
            } else {
                await axios.put(LMS_ENDPOINTS.carreras, payload);
            }
            setModalOpen(false);
            loadData();
        } catch (e) {
            console.error(e);
            triggerAlert('Error al guardar');
        }
    };

    const handleVerCarrera = (id) => {
        navigate(`/menu/lms/carrera/${id}`);
    };

    return (
        <div className="panel-sigma-main">
            <div className="profile-header-title">
                <h1 style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 700, color: '#1e293b' }}>{nombreTabla}</h1>
            </div>

            {/* Estadísticas Compactas con Línea Primaria */}
            <div className="stats-grid stats-grid-wrap" style={{ marginBottom: '2rem' }}>
                <div className="stat-card" style={{ borderLeft: '4px solid var(--color-primary)', padding: '0.6rem 1rem', display: 'flex', alignItems: 'center' }}>
                    <div className="stat-icon professional" style={{ backgroundColor: 'var(--color-primary)', color: 'white', width: '38px', height: '38px', minWidth: '38px' }}>
                        <FiLayers size={18} />
                    </div>
                    <div style={{ marginLeft: '0.8rem' }}>
                        <p className="stat-label" style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '0' }}>Total de carreras</p>
                        <p className="stat-value" style={{ fontSize: '1.2rem', fontWeight: 800, color: '#1e293b', lineHeight: 1 }}>{rows.length}</p>
                    </div>
                </div>
                <div className="stat-card" style={{ borderLeft: '4px solid var(--color-primary)', padding: '0.6rem 1rem', display: 'flex', alignItems: 'center' }}>
                    <div className="stat-icon professional" style={{ backgroundColor: 'var(--color-primary)', color: 'white', width: '38px', height: '38px', minWidth: '38px' }}>
                        <FiClock size={18} />
                    </div>
                    <div style={{ marginLeft: '0.8rem' }}>
                        <p className="stat-label" style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '0' }}>Borradores</p>
                        <p className="stat-value" style={{ fontSize: '1.2rem', fontWeight: 800, color: '#1e293b', lineHeight: 1 }}>
                            {rows.filter((x) => !x.publicado).length}
                        </p>
                    </div>
                </div>
                <div className="stat-card" style={{ borderLeft: '4px solid var(--color-primary)', padding: '0.6rem 1rem', display: 'flex', alignItems: 'center' }}>
                    <div className="stat-icon professional" style={{ backgroundColor: 'var(--color-primary)', color: 'white', width: '38px', height: '38px', minWidth: '38px' }}>
                        <FiCheckCircle size={18} />
                    </div>
                    <div style={{ marginLeft: '0.8rem' }}>
                        <p className="stat-label" style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '0' }}>Publicadas</p>
                        <p className="stat-value" style={{ fontSize: '1.2rem', fontWeight: 800, color: '#1e293b', lineHeight: 1 }}>
                            {rows.filter((x) => x.publicado).length}
                        </p>
                    </div>
                </div>
            </div>

            {/* UI de Toggle, Búsqueda y Filtros - Estilo "Referencia/Comentario" */}
            <div style={{ marginBottom: '2rem', filter: 'drop-shadow(0 4px 6px rgba(0,0,0,0.05))' }}>

                {/* 1. Header (Toggle Tabs) */}
                <div style={{
                    display: 'flex',
                    width: '100%',
                    backgroundColor: '#f1f5f9',
                    borderRadius: '12px 12px 0 0',
                    overflow: 'hidden',
                    padding: '0.5rem',
                    gap: '0.5rem',
                    border: '1px solid #e2e8f0',
                    borderBottom: 'none'
                }}>
                    {['todo', 'aprobada', 'en-curso', 'finalizado'].map((option) => (
                        <div
                            key={option}
                            onClick={() => setTab(option)}
                            style={{
                                flex: 1,
                                textAlign: 'center',
                                padding: '0.4rem 0',
                                fontSize: '0.9rem',
                                fontWeight: tab === option ? 700 : 500,
                                color: tab === option ? 'var(--color-primary-dark)' : '#64748b',
                                backgroundColor: tab === option ? '#ffffff' : 'transparent',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                userSelect: 'none',
                                textTransform: 'capitalize',
                                boxShadow: tab === option ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'
                            }}
                        >
                            {option === 'en-curso' ? 'En Curso' : option === 'finalizado' ? 'Publicada' : option === 'aprobada' ? 'Aprobada' : 'Todo'}
                        </div>
                    ))}
                </div>

                {/* 2. Body (Search) */}
                <div style={{
                    background: '#ffffff',
                    border: '1px solid #e2e8f0',
                    borderTop: 'none',
                    borderRadius: '0 0 12px 12px',
                    padding: '0.8rem 1rem',
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '1rem',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}>
                    {/* Búsqueda */}
                    <div style={{
                        flex: 1,
                        minWidth: '200px',
                        display: 'flex',
                        alignItems: 'center',
                        background: '#fff',
                        border: '1px solid #e2e8f0',
                        borderRadius: '999px',
                        padding: '0.4rem 1rem',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                    }}>
                        <FiSearch style={{ color: 'var(--color-primary-dark)', marginRight: '0.6rem', fontSize: '1rem' }} />
                        <input
                            type="text"
                            placeholder="Buscar carreras..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{ border: 'none', outline: 'none', background: 'transparent', width: '100%', fontSize: '0.9rem', color: '#1e293b' }}
                        />
                    </div>
                </div>
            </div>

            {/* Grid de carreras */}
            {loading ? (
                <div className="catalog-loading">Cargando carreras...</div>
            ) : (
                <div className="courses-grid fixed-cards">
                    {/* Tarjeta fantasma para crear nueva carrera - solo en tab Todo */}
                    {!searchTerm && tab === 'todo' && (
                        <article
                            className="course-card fixed-card phantom-create-card"
                            onClick={handleCreate}
                            style={{
                                border: '2px dashed #cbd5e1',
                                backgroundColor: '#f8fafc',
                                cursor: 'pointer',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                alignItems: 'center',
                                minHeight: '420px',
                                borderRadius: '24px',
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                boxShadow: 'none',
                                position: 'relative',
                                overflow: 'hidden'
                            }}
                        >
                            <div className="phantom-icon-wrapper" style={{
                                backgroundColor: 'var(--color-primary-100)',
                                color: 'var(--color-primary-dark)',
                                width: '80px',
                                height: '80px',
                                borderRadius: '28px',
                                border: '2.5px solid var(--color-primary-dark)',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                fontSize: '2.8rem',
                                marginBottom: '1.8rem',
                                transition: 'all 0.3s ease'
                            }}>
                                <FiPlusSquare />
                            </div>
                            <div style={{ textAlign: 'center', padding: '0 1.5rem' }}>
                                <h3 style={{ margin: 0, fontSize: '1.4rem', color: '#1e293b', fontWeight: 700 }}>Crear nueva carrera</h3>
                                <p style={{ margin: '0.8rem 0 0', fontSize: '1rem', color: '#64748b', lineHeight: 1.5 }}>Comienza a diseñar tu oferta educativa ahora mismo</p>
                            </div>
                        </article>
                    )}

                    {filtered.map((carrera) => {
                        const isPublicada = carrera.publicado;
                        const isAprobada = carrera.idCertificado !== null;
                        const statusVisuals = isPublicada
                            ? { bg: '#15803d', icon: <FiCheckCircle /> }   // Verde → Publicada
                            : isAprobada
                                ? { bg: '#0369a1', icon: <FiCheckCircle /> } // Azul → Aprobada
                                : { bg: '#ca8a04', icon: <FiClock /> };         // Amarillo → En curso

                        return (
                            <article key={carrera.idCarrera} className="course-card fixed-card" style={{
                                borderRadius: '24px',
                                minHeight: '420px',
                                border: `1px solid ${statusVisuals.bg}`,
                                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                                overflow: 'hidden'
                            }}>
                                <div className="course-thumb" style={{ height: '180px', overflow: 'hidden', position: 'relative' }}>
                                    {carrera.urlImagen ? (
                                        <img
                                            src={carrera.urlImagen.startsWith('http') ? carrera.urlImagen : `${SERVER_URL}${carrera.urlImagen}`}
                                            alt={carrera.nombre || 'Carrera'}
                                            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                                        />
                                    ) : (
                                        <div className="course-thumb-1" style={{ height: '100%', backgroundSize: 'cover' }} />
                                    )}

                                    {/* Triángulo de estatus en la esquina superior derecha */}
                                    <div style={{
                                        position: 'absolute', top: 0, right: 0, width: '60px', height: '60px', zIndex: 5, overflow: 'hidden'
                                    }}>
                                        <div style={{
                                            position: 'absolute', top: 0, right: 0, width: 0, height: 0,
                                            borderStyle: 'solid', borderWidth: '0 60px 60px 0',
                                            borderColor: `transparent ${statusVisuals.bg} transparent transparent`,
                                            filter: 'drop-shadow(-1px 1px 2px rgba(0,0,0,0.2))'
                                        }} />
                                        <div style={{
                                            position: 'absolute', top: '8px', right: '8px', color: 'white',
                                            fontSize: '1.5rem', display: 'flex', zIndex: 6
                                        }}>
                                            {statusVisuals.icon}
                                        </div>
                                    </div>
                                </div>

                                <div className="course-body" style={{
                                    padding: '1.5rem',
                                    flex: 1,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    backgroundColor: isPublicada ? '#f0fdf4' : isAprobada ? '#eff6ff' : '#fffbeb',
                                    transition: 'background-color 0.3s ease'
                                }}>
                                    <h3 className="course-title" style={{ fontSize: '1.15rem', fontWeight: 700, color: '#1e293b', marginBottom: '0.6rem', height: '1.4em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {carrera.nombre}
                                    </h3>

                                    <div className="course-meta" style={{ marginBottom: '1rem' }}>
                                        {isPublicada ? (
                                            <span className="status-pill completed" style={{ fontSize: '0.75rem', padding: '4px 10px' }}>
                                                <FiCheckCircle /> Publicada
                                            </span>
                                        ) : isAprobada ? (
                                            <span className="status-pill approved" style={{ fontSize: '0.75rem', padding: '4px 10px', backgroundColor: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe' }}>
                                                <FiCheckCircle /> Aprobado
                                            </span>
                                        ) : (
                                            <span className="status-pill in-progress" style={{ fontSize: '0.75rem', padding: '4px 10px', backgroundColor: '#fef3c7', color: '#92400e' }}>
                                                <FiClock /> En Curso
                                            </span>
                                        )}
                                    </div>

                                    <div className="course-actions" style={{ marginTop: 'auto', display: 'flex', gap: '8px' }}>
                                        {!isPublicada && (
                                            <button
                                                type="button"
                                                className="btn-cancel"
                                                style={{ 
                                                    flexShrink: 0, 
                                                    padding: '0.4rem 0.6rem',
                                                    display: 'flex', 
                                                    alignItems: 'center', 
                                                    justifyContent: 'center',
                                                    border: `1px solid ${statusVisuals.bg}`,
                                                    backgroundColor: isAprobada ? '#eff6ff' : '#fffbeb',
                                                    borderRadius: '8px'
                                                }}
                                                onClick={() => handleDelete(carrera)}
                                                title="Eliminar carrera"
                                            >
                                                <img src={sigmaDelete} alt="" style={{ width: '24px', height: '24px' }} />
                                            </button>
                                        )}

                                        <button
                                            type="button"
                                            className="btn-action"
                                            style={{
                                                flex: 1,
                                                fontSize: '13px',
                                                padding: '0.4rem 1rem',
                                                cursor: 'pointer',
                                                transition: '0.2s',
                                                fontFamily: "'Outfit', sans-serif",
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}
                                            onClick={() => handleVerCarrera(carrera.idCarrera)}
                                        >
                                            Ver carrera
                                        </button>
                                    </div>
                                </div>
                            </article>
                        );
                    })}
                </div>
            )}

            <CarrerasModal
                open={modalOpen}
                mode={modalMode}
                data={rows.find(r => r.idCarrera === selectedId)}
                onClose={() => setModalOpen(false)}
                onSubmit={handleSubmit}
            />

            <ConfirmDeleteDialog
                open={deleteDialog.open}
                onClose={() => setDeleteDialog({ open: false, item: null })}
                onConfirm={confirmDelete}
                roleName={deleteDialog.item?.nombre || 'esta carrera'}
            />

            <AlertDialog
                open={alertConfig.open}
                title={alertConfig.title}
                message={alertConfig.message}
                variant={alertConfig.variant}
                onClose={() => setAlertConfig({ ...alertConfig, open: false })}
            />
        </div>
    );
}
